/**
 * Service class for handling the loading state of a sheet.
 * This class manages the initialization, modal opening, and promise handling.
 */
class SheetLoadedService {
    /**
     * Constructor for SheetLoadedService.
     * @param {Object} $bridges - The bridges service.
     * @param {Object} $q - The AngularJS promise service.
     * @param {Object} $timeout - The AngularJS timeout service.
     * @param {Object} $uibModal - The AngularUI Bootstrap modal service.
     * @param {Object} $config - The configuration service.
     */
    constructor($bridges, $q, $timeout, $uibModal, $config) {
        this.$bridges = $bridges;
        this.$q = $q;
        this.$timeout = $timeout;
        this.$uibModal = $uibModal;
        this.$config = $config;

        // Meta setup
        this._logIdentifier = 'SheetLoadedService';
        this.$logger = this.$bridges.logger();
        this.$logger.info(this, 'initialize');

        this._defer = this.$q.defer();
        this._isInit = false;
        this._promises = 0;
        this._promisesReady = 0;
        this._isReady = false;
        this._loadingModalInstance = undefined;
        this._promiseTime = undefined;
    }

    /**
     * Opens the loading modal if it is not already open.
     * @returns {boolean} - Returns false if the modal is already open.
     */
    openLoadingModal() {
        if (angular.isDefined(this._loadingModalInstance)) return false;
        this._loadingModalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/components/modal/loadingModal.html",
            windowClass: "loading-layout-manager-modal",
            keyboard: false,
            backdrop: 'static',
            controller: function ($scope, $uibModalInstance) {
                $scope.$on('$routeChangeStart', function (next, current) {
                    $uibModalInstance.close();
                });
            }
        });
    }

    /**
     * Initializes the promise and related states.
     * @returns {boolean} - Returns false if already initialized.
     */
    initPromise() {
        if (this._isInit) {
            return false;
        }
        this._isInit = true;
        this._defer.resolve();
        this._defer = this.$q.defer();
        this._promises = 0;
        this._promisesReady = 0;
        this._isReady = false;
        if (angular.isDefined(this._promiseTime)) {
            this.$timeout.cancel(this._promiseTime);
        }
        this._promiseTime = this.$timeout(() => {
            this._defer.resolve();
            //console.log('##### CLOSE AFTER 7000 #####');
        }, 7000);

        this.$timeout(() => {
            //console.log('##### CLOSE #####', this._promises)
            if (this._promises === 0) {
                //console.log('##### CLOSE HALLO #####');
                this.$timeout.cancel(this._promiseTime);
                this._isInit = false;
                this._defer.resolve();
            }
        }, 1000);

        if (isCMS && !this.$config.isPDFMode()) {
            this.openLoadingModal();
            this._defer.promise.then(() => {
                if (angular.isDefined(this._loadingModalInstance)) {
                    this._loadingModalInstance.dismiss('cancel');
                    this._loadingModalInstance = undefined;
                }
            });
        }
    }

    /**
     * Waits for a promise to resolve and updates the state accordingly.
     * @param {Promise} value - The promise to wait for.
     * @param {string} objectName - The name of the object associated with the promise.
     * @returns {boolean} - Returns false if in PDF mode.
     */
    wait(value, objectName) {
        //console.log(value, '#####', objectName)
        if (this.$config.isPDFMode()) {
            this._defer.resolve();
            return false;
        }
        this._promises++;
        value.then(() => {
            this._promisesReady++;
            //console.log('##### OBJECT READY #####', this._promisesReady, '###_promises:', this._promises, objectName)
            this.$timeout(() => {
                if (this._promises === this._promisesReady) {
                    //console.log('##### CLOSE ON READY #####')
                    this._isReady = true;
                    this._isInit = false;
                    this._defer.resolve();
                    if (angular.isDefined(this._promiseTime)) {
                        this.$timeout.cancel(this._promiseTime);
                    }
                }
            });
        });
    }

    /**
     * Returns the current promise.
     * @returns {Promise} - The current promise.
     */
    getPromise() {
        if (this._isReady) {
            this._defer.resolve();
        }
        return this._defer.promise;
    }
}

export default SheetLoadedService;